import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, X } from "lucide-react";
import { api } from "../../lib/api";

const PLANS = [
  { key: "reset_monthly", name: "Reset", blurb: "Understand the Business" },
  { key: "momentum_monthly", name: "Momentum", blurb: "Improve the Business" },
  { key: "elevate_monthly", name: "Elevate", blurb: "Transform and Scale the Business" },
  { key: "portal_access_monthly", name: "Client Portal Access", blurb: "Self-service — track your own expenses. No financial diagnostic." },
];

const field = "w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm focus:border-[#c9a961] focus:outline-none";
const label = "block text-[9px] uppercase tracking-[0.18em] text-[#1e3d3a]/45 mb-1";

export default function ProposePlanForm({ onSent, onClose, initial = {} }) {
  const [form, setForm] = useState({ name: "", email: "", business_name: "", plan_lookup_key: "momentum_monthly", amount: "", billing: "monthly", message: "", ...initial });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!form.name.trim() || !form.email.includes("@")) { toast.error("Enter the client's name and a valid email."); return; }
    if (!amt || amt <= 0) { toast.error("Enter a price greater than $0."); return; }
    setBusy(true);
    try {
      await api.post("/admin-auth/subscriptions/propose", { ...form, amount: amt });
      toast.success(`${PLANS.find((p) => p.key === form.plan_lookup_key)?.name} plan sent to ${form.email.trim()}.`);
      onSent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the plan.");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="propose-plan-form" className="rounded-2xl border border-[#c9a961]/50 bg-[#eae7de]/40 p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#a3823f] font-bold">Send a plan to a client</p>
          <p className="mt-1 text-sm text-[#1e3d3a]/65">Pick the plan you think fits, name the price, and we'll email them a secure Stripe link.</p>
        </div>
        <button type="button" data-testid="propose-plan-close" onClick={onClose} className="text-[#1e3d3a]/40 hover:text-[#1e3d3a] transition-colors duration-300"><X className="h-4 w-4" /></button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div><label className={label}>Client name</label><input data-testid="propose-name" className={field} value={form.name} onChange={set("name")} placeholder="Jane Doe" /></div>
        <div><label className={label}>Client email</label><input data-testid="propose-email" type="email" className={field} value={form.email} onChange={set("email")} placeholder="jane@business.com" /></div>
        <div><label className={label}>Business (optional)</label><input data-testid="propose-business" className={field} value={form.business_name} onChange={set("business_name")} placeholder="Acme Plumbing" /></div>
      </div>

      <div>
        <label className={label}>Recommended plan</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {PLANS.map((p) => {
            const on = form.plan_lookup_key === p.key;
            return (
              <button key={p.key} type="button" data-testid={`propose-plan-${p.key}`} onClick={() => setForm((f) => ({ ...f, plan_lookup_key: p.key }))}
                className={`rounded-xl border p-3 text-left transition-colors duration-300 ${on ? "border-[#1e3d3a] bg-[#1e3d3a] text-white" : "border-[#1e3d3a]/15 bg-white text-[#1e3d3a] hover:border-[#c9a961]"}`}>
                <p className="font-head text-base">{p.name}</p>
                <p className={`text-[11px] mt-0.5 ${on ? "text-[#c9a961]" : "text-[#1e3d3a]/50"}`}>{p.blurb}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <label className={label}>Amount (USD)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#1e3d3a]/45">$</span>
            <input data-testid="propose-amount" type="number" min="1" step="1" className={`${field} w-36 pl-7`} value={form.amount} onChange={set("amount")} placeholder="0" />
          </div>
        </div>
        <div>
          <label className={label}>Billing</label>
          <div className="inline-flex rounded-full border border-[#1e3d3a]/15 bg-white p-1">
            {[["monthly", "Monthly"], ["one_time", "One-time"]].map(([k, t]) => (
              <button key={k} type="button" data-testid={`propose-billing-${k}`} onClick={() => setForm((f) => ({ ...f, billing: k }))}
                className={`rounded-full px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] transition-colors duration-300 ${form.billing === k ? "bg-[#1e3d3a] text-white" : "text-[#1e3d3a]/55"}`}>{t}</button>
            ))}
          </div>
        </div>
      </div>

      <div>
        <label className={label}>Personal note (goes in the email, optional)</label>
        <textarea data-testid="propose-message" rows={2} className={field} value={form.message} onChange={set("message")}
          placeholder="After our call, Momentum is the right fit to get your BBB rating and cash flow back on track." />
      </div>

      <div className="flex justify-end">
        <button type="submit" data-testid="propose-submit" disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-5 py-2.5 text-[11px] uppercase tracking-[0.14em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send plan & payment link
        </button>
      </div>
    </form>
  );
}
